/**
 * Auth API Service
 * Handles JWT authentication against the Skillform backend
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const TOKEN_KEYS = {
  access: 'sf_exam_access_token',
  refresh: 'sf_exam_refresh_token',
};

// ── Token helpers ──────────────────────────────────────────────

export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEYS.access);
}

export function getRefreshToken() {
  return localStorage.getItem(TOKEN_KEYS.refresh);
}

export function setTokens(access, refresh) {
  localStorage.setItem(TOKEN_KEYS.access, access);
  localStorage.setItem(TOKEN_KEYS.refresh, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.access);
  localStorage.removeItem(TOKEN_KEYS.refresh);
}

// ── API calls ──────────────────────────────────────────────────

/**
 * Login with email + password → returns JWT tokens + user data
 */
export async function login(email, password) {
  const response = await fetch(`${API_BASE_URL}/api/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Invalid email or password');
  }

  const data = await response.json();
  setTokens(data.access, data.refresh);
  return data;
}

/**
 * Refresh the access token using the stored refresh token
 */
export async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error('No refresh token');

  const response = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error('Session expired');
  }

  const data = await response.json();
  setTokens(data.access, data.refresh || refresh);
  return data;
}

/**
 * Fetch the current user's profile
 */
export async function fetchMe() {
  return authFetch('/api/users/me/');
}

// ── Authenticated fetch wrapper ────────────────────────────────

/**
 * Wrapper around fetch that:
 * 1. Adds Authorization: Bearer header
 * 2. Auto-refreshes on 401 and retries once
 */
export async function authFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const doFetch = (token) =>
    fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });

  let token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  let response = await doFetch(token);

  // If 401, try refreshing the token once
  if (response.status === 401) {
    try {
      const refreshed = await refreshAccessToken();
      token = refreshed.access;
      response = await doFetch(token);
    } catch {
      clearTokens();
      throw new Error('Session expired');
    }
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed: ${response.status}`);
  }

  return response.json();
}
